import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { useMediaQuery } from 'src/shared/hooks';
import useMobileMenu from 'src/shared/store/useMobileMenu';

import { cn, Tooltip } from 'src/shared/components/ui';

import { Nav } from '../../ui';

interface SidebarItemProps {
  icon: JSX.Element;
  title: string;
  path: string;
  isOpen: boolean;
  className?: string;
  isLongTitle?: boolean;
  tooltipContent?: string;
  isProduct?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  title,
  path,
  isOpen,
  className,
  isLongTitle,
  tooltipContent
}) => {
  const { pathname } = useLocation();

  const isMobile = useMediaQuery();
  const toggleMobileMenu = useMobileMenu((state) => state.toggle);
  const { tab } = useParams<{ tab?: string }>();

  const isActive = useMemo(() => {
    const cleanPath = path.split('?')[0];

    if (tab) {
      return pathname === `${cleanPath}/${tab}`;
    }

    return pathname === cleanPath;
  }, [pathname, path, tab]);

  // Construct the full path with parameters
  const fullPath = useMemo(() => {
    if (tab) {
      return `${path}?tab=${tab}`;
    }

    return path;
  }, [path, tab]);

  const onNavigate = () => {
    if (isMobile) {
      toggleMobileMenu();
    }
  };

  const isTooltipVisible = useMemo(() => {
    if (isLongTitle) {
      return false;
    }

    return isOpen;
  }, [isOpen, isLongTitle]);

  return (
    <Tooltip
      color="secondary"
      content={tooltipContent ?? title}
      placement="right-start"
      classNames={{
        content: 'bg-white rounded-lg !border-none h-8 top-1',
        base: 'rounded-sm !border-none top-1'
      }}
      shadow="lg"
      hidden={isTooltipVisible}
      closeDelay={0}
    >
      <Nav
        as={Link}
        color={isActive ? 'active' : 'nav'}
        className={cn('justify-start relative p-0 min-w-max', className)}
        onPress={onNavigate}
        fullWidth
        to={fullPath}
      >
        <div className="absolute left-3">{icon}</div>
        <motion.div
          initial={!isOpen && { maxWidth: 0, opacity: 0 }}
          animate={{ maxWidth: isOpen ? '100%' : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden left-10 whitespace-nowrap absolute"
        >
          <span>{title}</span>
        </motion.div>
      </Nav>
    </Tooltip>
  );
};

export default SidebarItem;
